import React from 'react';
import { useSelector } from 'react-redux';
import { isVideoUrl } from '../../Services/ApiService';
import SystemDataService from '../../Services/SystemDataService';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate';
import Image from '../../Utils/Image/image-component';
import VideoComponent from '../../Utils/Video/video-component';
import MapFrame from '../../Utils/Map/map-frame-component';
import { ButtonPrimary, ButtonSecondary } from '../../Utils/Buttons/button-component';

export default function LocationsHeroSection({ data }) {
    const scrollOrNavigate = useScrollOrNavigate();
    const systemData = useSelector((state) => state.systemData || {});
    const { dir, links = [], language } = systemData;
    const phoneData = SystemDataService.byLabel(links, 'Phone');

    if (!data) return null;

    // Destructure core identity and geo data
    const { name, hero, areasServed, zones, geo } = data;
    const heroMedia = hero?.imageDesktop || data.image;
    const heroTitle = hero?.title || data.name;
    const isVideo = isVideoUrl(heroMedia);
    
    const titleParts = heroTitle?.includes(' in ') ? heroTitle.split(' in ') : [heroTitle, name || ""];
    const cityName = titleParts[1] || name || "";
    const zipCodes = areasServed?.zips || "";

    return (
        <section className="relative w-full h-screen flex items-center justify-center bg-white dark:bg-neutral-950 font-assistant overflow-hidden">

            {/* --- LAYER 1: FULL-SCREEN MAP --- */}
            <div className="absolute inset-0 z-0">
                <MapFrame
                    url={geo?.mapUrl || "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3591423.582857467!2d-82.450098!3d27.994402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1700000000000"}
                    customData={{ zones: zones || [] }} 
                    interactive={true}
                    className="opacity-70 lg:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white dark:from-neutral-950/10 dark:to-neutral-950 pointer-events-none" />
            </div>

            {/* --- LAYER 2: THE FLOATING CARD --- */}
            <div className="relative z-10 w-full max-w-[1400px] px-6">
                <div className={`w-full max-w-xl lg:max-w-2xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur-3xl rounded-[40px] shadow-2xl border border-white dark:border-white/5 overflow-hidden transition-all duration-500 ${dir === 'rtl' ? 'mr-auto' : 'ml-0'}`}>

                    {/* Image Header: Hidden on mobile to keep map visible */}
                    {heroMedia && (
                        <div className="hidden md:block relative w-full h-48 lg:h-64 overflow-hidden border-b border-neutral-100 dark:border-white/5">
                            {isVideo ? (
                                <VideoComponent video={heroMedia} className="w-full h-full object-cover" />
                            ) : (
                                <Image src={heroMedia} alt={heroTitle} className="w-full h-full object-cover" />
                            )}
                        </div>
                    )}

                    {/* Content Area */}
                    <div className="p-8 lg:p-12 space-y-8">
                        <div className="space-y-3">
                            <h1 className="text-4xl lg:text-6xl font-black leading-[0.95] er text-primary-900 dark:text-white uppercase">
                                {titleParts[0]} <br />
                                <span className="text-secondary-500 italic">IN {cityName}</span>
                            </h1>
                            <p className="text-lg text-neutral-500 dark:text-neutral-400 font-medium ">
                                {hero?.description}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-4 pt-2">
                            <ButtonSecondary size="lg" onClick={scrollOrNavigate} className="flex-1 !rounded-2xl uppercase font-black tracking-widest active:scale-95 shadow-xl shadow-secondary-500/20">
                                {language === 'he' ? 'קבע שירות' : 'Book Service'}
                            </ButtonSecondary>
                            {phoneData && (
                                <ButtonPrimary size="lg" href={phoneData.url} variant="outline" className="flex-1 !rounded-2xl uppercase font-black tracking-widest active:scale-95">
                                    {language === 'he' ? 'התקשר' : 'Call Now'}
                                </ButtonPrimary>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}